import type { RecurrenceRule } from './schemas/booking';

interface ExpandedDate {
  start: Date;
  end: Date;
  seriesIndex: number;
}

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

const WEEKDAY_NAMES: Record<WeekdayKey, string> = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};

export function expandRecurrenceDates(
  startDate: Date,
  endDate: Date,
  rule: RecurrenceRule,
): ExpandedDate[] {
  const result: ExpandedDate[] = [];
  const duration = endDate.getTime() - startDate.getTime();
  const maxOccurrences = rule.count || 52;
  const until = rule.until ? new Date(rule.until) : null;

  let current = new Date(startDate);
  let index = 0;

  while (index < maxOccurrences) {
    if (until && current > until) break;

    let shouldInclude = false;

    switch (rule.frequency) {
      case 'daily':
        shouldInclude = true;
        break;

      case 'weekly': {
        if (rule.days && rule.days.length > 0) {
          const dayOfWeek = WEEKDAY_KEYS[current.getDay()];
          shouldInclude = dayOfWeek ? rule.days.includes(dayOfWeek) : false;
        } else {
          shouldInclude = true;
        }
        break;
      }

      case 'biweekly': {
        const dayOfWeekBI = WEEKDAY_KEYS[current.getDay()];
        const msPerWeek = 7 * 24 * 60 * 60 * 1000;
        const weeksSinceStart = Math.floor(
          (current.getTime() - startDate.getTime()) / msPerWeek,
        );
        if (weeksSinceStart % 2 === 0) {
          shouldInclude = dayOfWeekBI && rule.days
            ? rule.days.includes(dayOfWeekBI)
            : !rule.days;
        }
        break;
      }

      case 'monthly':
        shouldInclude =
          current.getDate() === startDate.getDate() &&
          current.getMonth() !== new Date(current.getTime() - 86400000).getMonth();
        break;

      case 'custom': {
        if (rule.days && rule.days.length > 0) {
          const dayOfWeekCustom = WEEKDAY_KEYS[current.getDay()];
          shouldInclude = dayOfWeekCustom ? rule.days.includes(dayOfWeekCustom) : false;
        }
        break;
      }
    }

    if (shouldInclude) {
      const slotEnd = new Date(current.getTime() + duration);
      result.push({
        start: new Date(current),
        end: slotEnd,
        seriesIndex: index,
      });
      index++;
    }

    current = new Date(current.getTime() + 24 * 60 * 60 * 1000);
  }

  return result;
}

export function generateRecurrencePreview(rule: RecurrenceRule, until?: string): string {
  const parts: string[] = [];

  switch (rule.frequency) {
    case 'daily':
      parts.push('Every day');
      break;

    case 'weekly':
      if (rule.days && rule.days.length > 0) {
        const dayNames = rule.days.map((d) => WEEKDAY_NAMES[d]).join(' and ');
        parts.push(`Every ${dayNames}`);
      } else {
        parts.push('Every week');
      }
      break;

    case 'biweekly':
      if (rule.days && rule.days.length > 0) {
        const dayNames = rule.days.map((d) => WEEKDAY_NAMES[d]).join(' and ');
        parts.push(`Every other ${dayNames}`);
      } else {
        parts.push('Every 2 weeks');
      }
      break;

    case 'monthly':
      parts.push('Monthly');
      break;

    case 'custom':
      if (rule.days && rule.days.length > 0) {
        const dayNames = rule.days.map((d) => WEEKDAY_NAMES[d]).join(' and ');
        parts.push(`On ${dayNames}`);
      } else {
        parts.push('Custom');
      }
      break;
  }

  if (rule.count) {
    parts.push(`for ${rule.count} class${rule.count === 1 ? '' : 'es'}`);
  } else if (until) {
    const date = new Date(until);
    const formatted = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    parts.push(`until ${formatted}`);
  }

  return parts.join(' ');
}

export function getRecurrenceCountEstimate(rule: RecurrenceRule): number {
  if (rule.count) {
    return rule.count;
  }
  // Default to showing 8 weeks worth as estimate
  return rule.frequency === 'daily' ? 56 : rule.frequency === 'weekly' || rule.frequency === 'biweekly' ? 8 : 12;
}
